import { makeStyles } from "tss-react/mui"
import { faLock } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons"
import { Checkbox, IconButton, lighten, Stack, Tooltip, Typography } from "@mui/material"
import { ResourceDetailsDialogParams } from "../../../../dialogs/resourceDetailsDialog/ResourceDetailsDialog"
import { NullableCalendarEvent, TimelineResourceLabelContent } from "../calendar/fullCalendarTypes"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { SearchableText } from "../../../../search/Searchable"

const useStyles = makeStyles()((theme) => ({
    checkbox: {
        padding: ".25rem",
    },
    dimmed: {
        color: theme.palette.tailwind.slate[400],
    },
    resourceLabelText: {
        display: "block",
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
    },
    paddingLeft: {
        paddingLeft: ".5rem",
    },
    buttonWrap: {
        paddingLeft: "1rem",
        cursor: "auto",
    },
    iconButton: {
        padding: "0",
    },
    icon: {
        color: lighten(theme.palette.tailwind.slate[400], 0.4),
    },
}))

type ResourceCalendarLabelContentProps = Readonly<{
    resourceContent: TimelineResourceLabelContent
    searchRegex: RegExp | undefined
    localEvent?: NullableCalendarEvent
    toggleResourceSelection?: (resourceId: string) => void
    isLocked?: boolean
    handleOpenDetails?: (res: ResourceDetailsDialogParams) => void
}>

export function ResourceCalendarLabelContent({
    localEvent,
    resourceContent,
    searchRegex,
    toggleResourceSelection,
    isLocked = false,
    handleOpenDetails,
}: ResourceCalendarLabelContentProps) {
    const { classes, cx } = useStyles()
    const { extendedProps, id, title } = resourceContent.resource

    const isChecked = !!localEvent?.resourceIds.includes(id)
    const isDimmed = !!extendedProps.isDimmed

    const handlePreventClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation()
    }

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        handleOpenDetails && handleOpenDetails({ title, ...extendedProps })
    }

    const handleRowClick = () => {
        if (isLocked || !toggleResourceSelection) return
        toggleResourceSelection?.(id)
    }

    return (
        <Stack
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="nowrap"
            gap="1rem"
            onClick={handleRowClick}
        >
            <Stack flexDirection="row" alignItems="center" minWidth={0}>
                {!isLocked && toggleResourceSelection && (
                    <Checkbox checked={isChecked} size="small" className={classes.checkbox} />
                )}
                <SearchableText search={searchRegex}>
                    <Typography
                        variant="body1"
                        className={cx(classes.resourceLabelText, {
                            [classes.dimmed]: isDimmed,
                            [classes.paddingLeft]: !toggleResourceSelection || isLocked,
                        })}
                    >
                        {title}
                    </Typography>
                </SearchableText>
            </Stack>
            <Stack
                flexDirection="row"
                alignItems="center"
                gap=".75rem"
                className={classes.buttonWrap}
                onClick={handlePreventClick}
            >
                {extendedProps?.accessControl?.hasAccess === false && (
                    <LocalizedStrict id="create-order-tooltip-resource-lock-button" attrs={{ title: true }}>
                        <Tooltip title="Can only be booked by selected users">
                            <FontAwesomeIcon icon={faLock} className={classes.icon} />
                        </Tooltip>
                    </LocalizedStrict>
                )}
                <LocalizedStrict id="create-order-tooltip-resource-info-button" attrs={{ title: true }}>
                    <Tooltip title="Show resource details">
                        <IconButton onClick={handleClick} className={classes.iconButton} size="small">
                            <FontAwesomeIcon icon={faCircleInfo} className={classes.icon} />
                        </IconButton>
                    </Tooltip>
                </LocalizedStrict>
            </Stack>
        </Stack>
    )
}
